import axios from "axios";
import {
  getAuthHeaders,
  getIdToken,
  isAccountSwitchPending,
  clearProductCsrfToken,
  ensureFreshIdToken,
} from "@xinosolutions/xs-auth-react";
import { baseUrl } from "../config/config";
import { createLogger } from "../utils/logger";
import { createCsrfTokenManager } from "../utils/csrfTokenManager";
import {
  buildInvokeRequestConfig,
  getErrorResponseMessage,
  isAxiosCancelError,
  isCanceledResponse,
  isCsrfForbiddenError,
} from "../utils/invokeApiUtils";

const log = createLogger("invokeApi");

axios.defaults.headers.post["Content-Type"] = "application/json";

const APP_CONTEXT = "associate";
const csrf = createCsrfTokenManager({
  axios,
  baseUrl,
  appContext: APP_CONTEXT,
});

export const clearCsrfToken = csrf.clearCsrfToken;

function isAuthAttemptRequest(url) {
  if (typeof url !== "string") return false;
  return (
    url.includes("/api/member_session") ||
    url.includes("/api/member_init")
  );
}

axios.interceptors.request.use(async (config) => {
  if (!config.skip401Refresh && !isAuthAttemptRequest(config.url)) {
    await ensureFreshIdToken();
  }
  const auth = getAuthHeaders();
  if (auth.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = auth.Authorization;
  }
  return csrf.requestInterceptor(config);
});

/** @param {import("axios").AxiosError} error */
export function isRequestCanceled(error) {
  return isAxiosCancelError(error, axios);
}

export { isCanceledResponse };

axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error.response?.status;
    const original = error.config;
    const data = error.response?.data;
    const msg = getErrorResponseMessage(data);

    if (
      isCsrfForbiddenError(status, msg) &&
      original &&
      !original.__csrfRetry
    ) {
      clearCsrfToken();
      original.__csrfRetry = true;
      return axios(original);
    }

    if (
      status === 401 &&
      original &&
      !original.skip401Refresh &&
      !original.__authRetry &&
      !isAuthAttemptRequest(original.url)
    ) {
      clearCsrfToken();
      clearProductCsrfToken();
      original.__authRetry = true;
      const token = await ensureFreshIdToken({ force: true });
      if (token) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${token}`;
        return axios(original);
      }
      if (!getIdToken() && !isAccountSwitchPending() && typeof window !== "undefined") {
        window.dispatchEvent(new Event("user-logout"));
      }
    }

    return Promise.reject(error);
  }
);

export async function invokeApi({
  path,
  method = "GET",
  postData = {},
  headers = {},
  signal = null,
  skip401Handler = false,
}) {
  const config = buildInvokeRequestConfig({
    path,
    method,
    postData,
    headers,
    signal,
    baseUrl,
    appContext: APP_CONTEXT,
  });

  if (arguments[0].onUploadProgress) {
    config.onUploadProgress = arguments[0].onUploadProgress;
  }
  
  if (arguments[0].timeout) {
    config.timeout = arguments[0].timeout;
  }

  if (arguments[0].responseType) {
    config.responseType = arguments[0].responseType;
  }

  if (skip401Handler) {
    config.skip401Refresh = true;
  }

  try {
    const response = await axios(config);
    return response.data;
  } catch (error) {
    if (isRequestCanceled(error)) {
      return { canceled: true };
    }
    log.error("invokeApi failed", { path, message: error?.message });
    throw error;
  }
}
